import { AnyAction } from 'redux';
import { Injectable } from '@angular/core';
import { v1 as uuidv1 } from 'uuid';
import { App, TierartEnum } from '@ps20-frontend/frontend-models';
import { AnimalIllness } from '@ps20/bff-models';
import { TgvProduktSnapshot } from './models/angebot.model';
import { TgvSubprodukt } from './models/tgvSubprodukt';
import {
  TgvKatalog,
  TgvTierForm,
  TgvTierUmfang,
  TgvVorschaeden,
  TgvVorschaedenState,
} from './models/tiergruppe.model';

export interface TgvAction<T = undefined> extends AnyAction {
  payload?: T;
}

export interface PositionAddedPayload {
  subprodukt: TgvSubprodukt;
  positionId: string;
}

export interface TierartSelectedPayload {
  cardId: string;
  tierart: TierartEnum;
}

export interface UmfangChangedPayload {
  cardId: string;
  umfang: TgvTierUmfang;
}

export interface TierFormChangedPayload {
  cardId: string;
  patch: Partial<TgvTierForm>;
}

export interface IllnessToggledPayload {
  cardId: string;
  illness: AnimalIllness;
  checked: boolean;
}

export interface AngebotsIdAssignedPayload {
  cardId: string;
  angebotsId: string;
}

export interface ChildActionRequestedPayload {
  child: { appId: App; angebotsId?: string };
  inner: AnyAction;
}

export type TgvPricingStatus = 'queued' | 'running' | 'priced' | 'failed';

export interface PricingStatusChangedPayload {
  key: string;
  status: TgvPricingStatus;
  requestId?: string;
}

export interface ChildPricingForwardedPayload {
  key: string;
  requestId: string;
  reload?: boolean;
}

export interface ChildPricingForwardQueuedPayload {
  key: string;
  inner: AnyAction;
}

export interface VorschaedenChangedPayload {
  thh: keyof TgvVorschaedenState;
  patch: Partial<TgvVorschaeden>;
}

export type TgvPricingIssueKind = 'failed' | 'stuck';

export type TgvPricingIssueAnswer = 'retry' | 'wait' | 'leave';

export interface PricingIssueAnsweredPayload {
  answer: TgvPricingIssueAnswer;
  kind: TgvPricingIssueKind;
  keys?: string[];
}

export interface PricingIssueReportedPayload {
  kind: TgvPricingIssueKind;
  keys: string[];
  errorCodes: Record<string, string>;
}

export interface TakeOverAgainPayload {
  keys: string[];
  priceLast: boolean;
}

@Injectable()
export class TgvActions {
  static readonly TGV_ZURUECK_ZUM_SHOP_MODAL_OPEN = 'TGV_ZURUECK_ZUM_SHOP_MODAL_OPEN';
  static readonly TGV_ZURUECK_ZUM_SHOP_MODAL_CLOSE = 'TGV_ZURUECK_ZUM_SHOP_MODAL_CLOSE';
  static readonly TGV_SUBPRODUKT_SELECTED = 'TGV_SUBPRODUKT_SELECTED';
  static readonly TGV_POSITION_ADDED = 'TGV_POSITION_ADDED';
  static readonly TGV_TIERART_SELECTED = 'TGV_TIERART_SELECTED';
  static readonly TGV_UMFANG_CHANGED = 'TGV_UMFANG_CHANGED';
  static readonly TGV_TIER_FORM_CHANGED = 'TGV_TIER_FORM_CHANGED';
  static readonly TGV_ILLNESS_TOGGLED = 'TGV_ILLNESS_TOGGLED';
  static readonly TGV_TIER_ADDED = 'TGV_TIER_ADDED';
  static readonly TGV_TIER_REMOVED = 'TGV_TIER_REMOVED';
  static readonly TGV_JURISTISCH_RECEIVED = 'TGV_JURISTISCH_RECEIVED';
  static readonly TGV_VORSCHAEDEN_CHANGED = 'TGV_VORSCHAEDEN_CHANGED';
  static readonly TGV_KATALOG_REQUESTED = 'TGV_KATALOG_REQUESTED';
  static readonly TGV_KATALOG_RECEIVED = 'TGV_KATALOG_RECEIVED';
  static readonly TGV_KATALOG_REQUEST_FAILED = 'TGV_KATALOG_REQUEST_FAILED';
  static readonly TGV_ANGEBOTS_ID_CLEARED = 'TGV_ANGEBOTS_ID_CLEARED';
  static readonly TGV_FORM_SUBMITTED = 'TGV_FORM_SUBMITTED';
  static readonly TGV_ZUM_ANGEBOT_CLICKED = 'TGV_ZUM_ANGEBOT_CLICKED';
  static readonly TGV_ANGEBOT_REQUESTED = 'TGV_ANGEBOT_REQUESTED';
  static readonly TGV_INS_DASHBOARD_CLICKED = 'TGV_INS_DASHBOARD_CLICKED';
  static readonly TGV_TAKE_OVER_STARTED = 'TGV_TAKE_OVER_STARTED';
  static readonly TGV_TAKE_OVER_ENDED = 'TGV_TAKE_OVER_ENDED';
  static readonly TGV_PRODUKTE_RECEIVED = 'TGV_PRODUKTE_RECEIVED';
  static readonly TGV_ANGEBOTS_ID_ASSIGNED = 'TGV_ANGEBOTS_ID_ASSIGNED';
  static readonly TGV_THH_ANGELEGT = 'TGV_THH_ANGELEGT';
  static readonly TGV_UEBERNOMMEN = 'TGV_UEBERNOMMEN';
  static readonly TGV_ORPHANS_DEFERRED = 'TGV_ORPHANS_DEFERRED';
  static readonly TGV_TAKE_OVER_AGAIN_REQUESTED = 'TGV_TAKE_OVER_AGAIN_REQUESTED';
  static readonly TGV_CHILD_CREATING_ADDED = 'TGV_CHILD_CREATING_ADDED';
  static readonly TGV_CHILD_CREATING_REMOVED = 'TGV_CHILD_CREATING_REMOVED';
  static readonly TGV_PENDING_DELETION_MARKED = 'TGV_PENDING_DELETION_MARKED';
  static readonly TGV_PENDING_DELETION_CLEARED = 'TGV_PENDING_DELETION_CLEARED';
  static readonly TGV_ANGEBOT_OFFER_OPENED = 'TGV_ANGEBOT_OFFER_OPENED';
  static readonly TGV_ANGEBOT_EDIT_MARKED = 'TGV_ANGEBOT_EDIT_MARKED';
  static readonly TGV_ANGEBOT_OPENED = 'TGV_ANGEBOT_OPENED';
  static readonly TGV_ANGEBOT_SUMMARY_TOGGLED = 'TGV_ANGEBOT_SUMMARY_TOGGLED';
  static readonly TGV_ANGEBOT_ADOPT_REQUESTED = 'TGV_ANGEBOT_ADOPT_REQUESTED';
  static readonly TGV_ANGEBOT_ADOPTION_BLOCKED = 'TGV_ANGEBOT_ADOPTION_BLOCKED';
  static readonly TGV_ANGEBOT_COPY_TO_ALL_CLICKED = 'TGV_ANGEBOT_COPY_TO_ALL_CLICKED';
  static readonly TGV_ANGEBOT_COPY_TO_ALL_STARTED = 'TGV_ANGEBOT_COPY_TO_ALL_STARTED';
  static readonly TGV_ANGEBOT_COPY_TO_ALL_FINISHED = 'TGV_ANGEBOT_COPY_TO_ALL_FINISHED';
  static readonly TGV_ANGEBOT_OFFERS_VISITED = 'TGV_ANGEBOT_OFFERS_VISITED';
  static readonly TGV_CHILD_PRICING_FORWARDED = 'TGV_CHILD_PRICING_FORWARDED';
  static readonly TGV_CHILD_PRICING_FORWARD_QUEUED = 'TGV_CHILD_PRICING_FORWARD_QUEUED';
  static readonly TGV_PRODUCT_RELOAD_ENDED = 'TGV_PRODUCT_RELOAD_ENDED';
  static readonly TGV_THH_CLEARED = 'TGV_THH_CLEARED';
  static readonly TGV_RUN_FAILED = 'TGV_RUN_FAILED';
  static readonly TGV_CHILD_LINKS_HEALED = 'TGV_CHILD_LINKS_HEALED';
  static readonly TGV_ANGEBOT_CLOSED = 'TGV_ANGEBOT_CLOSED';
  static readonly TGV_PRICING_STATUS_CHANGED = 'TGV_PRICING_STATUS_CHANGED';
  static readonly TGV_PRICING_ATTEMPTS_RESET = 'TGV_PRICING_ATTEMPTS_RESET';
  static readonly TGV_CHILD_ACTION_REQUESTED = 'TGV_CHILD_ACTION_REQUESTED';
  static readonly TGV_EINGABE_TARGET_CONSUMED = 'TGV_EINGABE_TARGET_CONSUMED';
  static readonly TGV_PRICING_ISSUE_REPORTED = 'TGV_PRICING_ISSUE_REPORTED';
  static readonly TGV_PRICING_ISSUE_ANSWERED = 'TGV_PRICING_ISSUE_ANSWERED';
  static readonly TGV_PRICING_ISSUE_KEYS_WITHDRAWN = 'TGV_PRICING_ISSUE_KEYS_WITHDRAWN';

  childActionRequested(
    child: { appId: App; angebotsId?: string },
    inner: AnyAction,
  ): TgvAction<ChildActionRequestedPayload> {
    return { type: TgvActions.TGV_CHILD_ACTION_REQUESTED, payload: { child, inner } };
  }

  eingabeTargetConsumed(): TgvAction<void> {
    return { type: TgvActions.TGV_EINGABE_TARGET_CONSUMED };
  }

  pricingIssueReported(
    kind: TgvPricingIssueKind,
    keys: string[],
    errorCodes: Record<string, string> = {},
  ): TgvAction<PricingIssueReportedPayload> {
    return { type: TgvActions.TGV_PRICING_ISSUE_REPORTED, payload: { kind, keys, errorCodes } };
  }

  pricingIssueAnswered(
    answer: TgvPricingIssueAnswer,
    kind: TgvPricingIssueKind,
    keys?: string[],
  ): TgvAction<PricingIssueAnsweredPayload> {
    return {
      type: TgvActions.TGV_PRICING_ISSUE_ANSWERED,
      payload: keys === undefined ? { answer, kind } : { answer, kind, keys },
    };
  }

  pricingIssueKeysWithdrawn(keys: string[]): TgvAction<string[]> {
    return { type: TgvActions.TGV_PRICING_ISSUE_KEYS_WITHDRAWN, payload: keys };
  }

  angebotClosed(): TgvAction<void> {
    return { type: TgvActions.TGV_ANGEBOT_CLOSED };
  }

  pricingAttemptsReset(key: string): TgvAction<string> {
    return { type: TgvActions.TGV_PRICING_ATTEMPTS_RESET, payload: key };
  }

  pricingStatusChanged(
    key: string,
    status: TgvPricingStatus,
    requestId?: string,
  ): TgvAction<PricingStatusChangedPayload> {
    return {
      type: TgvActions.TGV_PRICING_STATUS_CHANGED,
      payload: requestId === undefined ? { key, status } : { key, status, requestId },
    };
  }

  childLinksHealed(keys: string[]): TgvAction<string[]> {
    return { type: TgvActions.TGV_CHILD_LINKS_HEALED, payload: keys };
  }

  runFailed(reason: string): TgvAction<string> {
    return { type: TgvActions.TGV_RUN_FAILED, payload: reason };
  }

  thhCleared(thh: keyof TgvVorschaedenState): TgvAction<keyof TgvVorschaedenState> {
    return { type: TgvActions.TGV_THH_CLEARED, payload: thh };
  }

  childCreatingAdded(key: string): TgvAction<string> {
    return { type: TgvActions.TGV_CHILD_CREATING_ADDED, payload: key };
  }

  childCreatingRemoved(key: string): TgvAction<string> {
    return { type: TgvActions.TGV_CHILD_CREATING_REMOVED, payload: key };
  }

  pendingDeletionMarked(keys: string[]): TgvAction<string[]> {
    return { type: TgvActions.TGV_PENDING_DELETION_MARKED, payload: keys };
  }

  pendingDeletionCleared(keys: string[]): TgvAction<string[]> {
    return { type: TgvActions.TGV_PENDING_DELETION_CLEARED, payload: keys };
  }

  angebotOfferOpened(key: string): TgvAction<string> {
    return { type: TgvActions.TGV_ANGEBOT_OFFER_OPENED, payload: key };
  }

  angebotEditMarked(key: string): TgvAction<string> {
    return { type: TgvActions.TGV_ANGEBOT_EDIT_MARKED, payload: key };
  }

  angebotOpened(): TgvAction<void> {
    return { type: TgvActions.TGV_ANGEBOT_OPENED };
  }

  angebotSummaryToggled(): TgvAction<void> {
    return { type: TgvActions.TGV_ANGEBOT_SUMMARY_TOGGLED };
  }

  angebotAdoptRequested(): TgvAction<void> {
    return { type: TgvActions.TGV_ANGEBOT_ADOPT_REQUESTED };
  }

  angebotAdoptionBlocked(): TgvAction<void> {
    return { type: TgvActions.TGV_ANGEBOT_ADOPTION_BLOCKED };
  }

  angebotCopyToAllClicked(key: string): TgvAction<string> {
    return { type: TgvActions.TGV_ANGEBOT_COPY_TO_ALL_CLICKED, payload: key };
  }

  angebotCopyToAllStarted(): TgvAction<void> {
    return { type: TgvActions.TGV_ANGEBOT_COPY_TO_ALL_STARTED };
  }

  angebotCopyToAllFinished(): TgvAction<void> {
    return { type: TgvActions.TGV_ANGEBOT_COPY_TO_ALL_FINISHED };
  }

  angebotOffersVisited(keys: string[]): TgvAction<string[]> {
    return { type: TgvActions.TGV_ANGEBOT_OFFERS_VISITED, payload: keys };
  }

  childPricingForwarded(
    key: string,
    requestId: string,
    reload?: boolean,
  ): TgvAction<ChildPricingForwardedPayload> {
    return {
      type: TgvActions.TGV_CHILD_PRICING_FORWARDED,
      payload: reload ? { key, requestId, reload } : { key, requestId },
    };
  }

  childPricingForwardQueued(
    key: string,
    inner: AnyAction,
  ): TgvAction<ChildPricingForwardQueuedPayload> {
    return { type: TgvActions.TGV_CHILD_PRICING_FORWARD_QUEUED, payload: { key, inner } };
  }

  productReloadEnded(key: string): TgvAction<string> {
    return { type: TgvActions.TGV_PRODUCT_RELOAD_ENDED, payload: key };
  }

  zurueckZumShopModalOpen(): TgvAction<void> {
    return { type: TgvActions.TGV_ZURUECK_ZUM_SHOP_MODAL_OPEN };
  }

  zurueckZumShopModalClose(): TgvAction<void> {
    return { type: TgvActions.TGV_ZURUECK_ZUM_SHOP_MODAL_CLOSE };
  }

  subproduktSelected(subprodukt: TgvSubprodukt): TgvAction<TgvSubprodukt> {
    return { type: TgvActions.TGV_SUBPRODUKT_SELECTED, payload: subprodukt };
  }

  tierartSelected(cardId: string, tierart: TierartEnum): TgvAction<TierartSelectedPayload> {
    return { type: TgvActions.TGV_TIERART_SELECTED, payload: { cardId, tierart } };
  }

  umfangChanged(cardId: string, umfang: TgvTierUmfang): TgvAction<UmfangChangedPayload> {
    return { type: TgvActions.TGV_UMFANG_CHANGED, payload: { cardId, umfang } };
  }

  tierFormChanged(cardId: string, patch: Partial<TgvTierForm>): TgvAction<TierFormChangedPayload> {
    return { type: TgvActions.TGV_TIER_FORM_CHANGED, payload: { cardId, patch } };
  }

  illnessToggled(
    cardId: string,
    illness: AnimalIllness,
    checked: boolean,
  ): TgvAction<IllnessToggledPayload> {
    return { type: TgvActions.TGV_ILLNESS_TOGGLED, payload: { cardId, illness, checked } };
  }

  tierAdded(): TgvAction<string> {
    return { type: TgvActions.TGV_TIER_ADDED, payload: uuidv1() };
  }

  tierRemoved(cardId: string): TgvAction<string> {
    return { type: TgvActions.TGV_TIER_REMOVED, payload: cardId };
  }

  juristischReceived(juristisch: boolean): TgvAction<boolean> {
    return { type: TgvActions.TGV_JURISTISCH_RECEIVED, payload: juristisch };
  }

  vorschaedenChanged(
    thh: keyof TgvVorschaedenState,
    patch: Partial<TgvVorschaeden>,
  ): TgvAction<VorschaedenChangedPayload> {
    return { type: TgvActions.TGV_VORSCHAEDEN_CHANGED, payload: { thh, patch } };
  }

  katalogRequested(): TgvAction<void> {
    return { type: TgvActions.TGV_KATALOG_REQUESTED };
  }

  katalogReceived(patch: Partial<TgvKatalog>): TgvAction<Partial<TgvKatalog>> {
    return { type: TgvActions.TGV_KATALOG_RECEIVED, payload: patch };
  }

  angebotsIdAssigned(cardId: string, angebotsId: string): TgvAction<AngebotsIdAssignedPayload> {
    return { type: TgvActions.TGV_ANGEBOTS_ID_ASSIGNED, payload: { cardId, angebotsId } };
  }

  angebotsIdCleared(cardId: string): TgvAction<string> {
    return { type: TgvActions.TGV_ANGEBOTS_ID_CLEARED, payload: cardId };
  }

  thhAngelegt(thh: keyof TgvVorschaedenState): TgvAction<keyof TgvVorschaedenState> {
    return { type: TgvActions.TGV_THH_ANGELEGT, payload: thh };
  }

  uebernommen(keys: string[]): TgvAction<string[]> {
    return { type: TgvActions.TGV_UEBERNOMMEN, payload: keys };
  }

  orphansDeferred(keys: string[]): TgvAction<string[]> {
    return { type: TgvActions.TGV_ORPHANS_DEFERRED, payload: keys };
  }

  takeOverAgainRequested(keys: string[], priceLast = false): TgvAction<TakeOverAgainPayload> {
    return { type: TgvActions.TGV_TAKE_OVER_AGAIN_REQUESTED, payload: { keys, priceLast } };
  }

  angebotRequested(): TgvAction<void> {
    return { type: TgvActions.TGV_ANGEBOT_REQUESTED };
  }

  insDashboardClicked(): TgvAction<void> {
    return { type: TgvActions.TGV_INS_DASHBOARD_CLICKED };
  }

  takeOverStarted(): TgvAction<void> {
    return { type: TgvActions.TGV_TAKE_OVER_STARTED };
  }

  takeOverEnded(): TgvAction<void> {
    return { type: TgvActions.TGV_TAKE_OVER_ENDED };
  }

  produkteReceived(produkte: TgvProduktSnapshot[]): TgvAction<TgvProduktSnapshot[]> {
    return { type: TgvActions.TGV_PRODUKTE_RECEIVED, payload: produkte };
  }

  /** The user tried to continue: from now on the missing answers are shown. */
  formSubmitted(): TgvAction<void> {
    return { type: TgvActions.TGV_FORM_SUBMITTED };
  }

  zumAngebotClicked(): TgvAction<void> {
    return { type: TgvActions.TGV_ZUM_ANGEBOT_CLICKED };
  }

  positionAdded(subprodukt: TgvSubprodukt): TgvAction<PositionAddedPayload> {
    return {
      type: TgvActions.TGV_POSITION_ADDED,
      payload: { subprodukt, positionId: uuidv1() },
    };
  }
}
